import { useId, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { hasRole, useAuthOptional } from '../lib/auth';
import { fmt } from '../lib/format';
import { humanize, when } from '../lib/labels';
import { ActionButton, ActionForm, ErrorBox, Load, Loading, Page, Section, Status, Table, useApi } from '../ui/kit';
import { LinkedinAccount } from '../ui/linkedin';
import { OtherWorkspace, useOtherWorkspace } from '../ui/connect-return';
import { VisitorSetup } from './visitors';
import { Meetings } from './settings';
import {
  CATEGORIES, CATEGORY_ORDER, INTEGRATIONS, integrationBySlug, onSite, STATUS_LABEL, type CategoryId, type Integration, type MarketStatus,
} from '../../../src/integrations/catalog';
import { logoSvg } from '../../../src/integrations/logo';

// The integrations marketplace (owner request 2026-10-23): every external app Outlevo connects to, as cards with a
// live status, and a page per integration with what it does, what syncs, the setup steps, who can connect and the
// real Connect or Manage action, wired to the flows each feature already has. The catalogue is the one the site
// renders outlevo.com/integrations/ from (src/integrations/catalog.ts); the state comes from GET /v1/integrations.

type Entry = { slug: string; status: MarketStatus; configured: boolean; connected: boolean; detail: string | null };
type Crm = { id: string; provider: string; status: string; external_account_id: string; connected_at: string; contacts_enabled: boolean; contacts_purpose: string | null; purge_after: string | null };
type Data = { connections: Crm[]; capabilities: any[]; available: string[]; marketplace?: Entry[] };

const CRM_NAME = (p: string) => (p === 'hubspot' ? 'HubSpot' : p === 'salesforce' ? 'Salesforce' : humanize(p));
const TONE: Record<MarketStatus, string> = { connected: 'good', available: 'good', included: 'good', pilot: 'neutral', coming_soon: 'neutral', not_set_up: 'neutral' };
const SITE = 'https://outlevo.com';

function Logo({ i, size = 28 }: { i: Integration; size?: number }) {
  // the SVG is built from the catalogue and the generated marks (src/integrations/logos.ts), never from user data
  return <span className="int-logo" style={{ width: size + 18, height: size + 18 }} dangerouslySetInnerHTML={{ __html: logoSvg(i, { size }) }} />;
}
const Badge = ({ s }: { s: MarketStatus }) => <span className={`chip chip-${TONE[s]}`}>{STATUS_LABEL[s]}</span>;

/** The state of each entry; without the API's marketplace field (an older server) every entry reads from the catalogue. */
const stateOf = (d: Data | undefined, i: Integration): Entry => d?.marketplace?.find((m) => m.slug === i.slug)
  ?? { slug: i.slug, status: i.availability === 'awaiting_approval' ? 'coming_soon' : i.included ? 'included' : 'available', configured: true, connected: false, detail: null };

/** Outside the app shell (tests, previews) there is no session: show the actions and let the API decide. */
const useCan = (roles: string[]) => { const a = useAuthOptional(); return !a?.me || hasRole(a.me, ...roles); };

export function Integrations() {
  const admin = useCan(['owner', 'admin']);
  const signedIn = Boolean(useAuthOptional()?.me);
  const q = useApi<Data>('/v1/integrations');
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(params.get('q') ?? '');
  const cat = params.get('category') as CategoryId | null;
  const searchId = useId();
  // back from a CRM's consent screen (/oauth/:provider/callback): the provider's own page says how it went
  const provider = params.get('provider');
  if (provider && params.get('status')) return <Navigate to={`/integrations/${provider}?${params.toString()}`} replace />;
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  // an entry that is part of another (LinkedIn's ad campaigns, inside LinkedIn Ads) is not a card of its own, unless connected
  const shown = INTEGRATIONS.filter((i) => (!i.partOf || (q.data && stateOf(q.data, i).status === 'connected')) && (!cat || i.category === cat)
    && words.every((w) => [i.name, i.vendor, i.tagline.t, CATEGORIES[i.category].label].join(' ').toLowerCase().includes(w)));
  const setCat = (c: CategoryId | null) => { const p = new URLSearchParams(params); if (c) p.set('category', c); else p.delete('category'); setParams(p, { replace: true }); };
  const connected = q.data ? INTEGRATIONS.filter((i) => stateOf(q.data, i).status === 'connected') : [];
  return (
    <Page title="Integrations">
      <p className="muted">Everything Outlevo connects to. Open one to see what it does, what syncs and how to connect it.</p>
      {q.error ? <ErrorBox error={q.error} retry={() => q.refetch()} /> : null}
      <div className="int-tools">
        <label htmlFor={searchId} className="sr-only">Search integrations</label>
        <input id={searchId} type="search" className="int-search" placeholder="Search integrations" value={text} onChange={(e) => setText(e.target.value)} />
        <div className="int-chips" role="group" aria-label="Categories">
          <button type="button" className="chip-btn" aria-pressed={!cat} onClick={() => setCat(null)}>All</button>
          {CATEGORY_ORDER.filter((c) => INTEGRATIONS.some((i) => i.category === c)).map((c) => (
            <button key={c} type="button" className="chip-btn" aria-pressed={cat === c} onClick={() => setCat(cat === c ? null : c)}>{CATEGORIES[c].label}</button>))}
        </div>
      </div>
      {q.isLoading ? <Loading /> : null}
      {connected.length && !cat && !words.length ? <p className="small muted">Connected: {connected.map((i) => i.name).join(', ')}.</p> : null}
      {shown.length ? (
        <ul className="int-grid" aria-label="Integrations">
          {shown.map((i) => {
            const s = stateOf(q.data, i);
            return (
              <li key={i.slug}>
                <Link className="int-card" to={`/integrations/${i.slug}`}>
                  <Logo i={i} />
                  <span className="int-card-body">
                    <span className="int-card-name">{i.name}</span>
                    <span className="int-card-line">{i.tagline.t}</span>
                    <span className="int-card-foot"><Badge s={s.status} />{s.detail ? <span className="small muted"> {s.detail}</span> : null}</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : <p className="muted" role="status">No integration matches. <button type="button" className="link" onClick={() => { setText(''); setCat(null); }}>Show all</button></p>}
      {admin && signedIn && q.data ? (
        <Section title="Capability registry" actions={<span className="small muted">live only after the provider confirms it</span>}>
          <Table caption="Capability registry" rows={q.data.capabilities} columns={[{ key: 'capability', render: (r) => humanize(r.capability) }, { key: 'jurisdiction', label: 'Where' }, 'state', { key: 'changed_at', label: 'Changed' }]} empty="Nothing connected yet." />
        </Section>
      ) : null}
    </Page>
  );
}

export function IntegrationDetail() {
  const { slug = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const i = integrationBySlug(slug);
  const q = useApi<Data>('/v1/integrations');
  const canConnect = useCan(i?.roles ?? ['owner', 'admin']);
  const elsewhere = useOtherWorkspace();
  if (!i) return <Page title="Integration not found"><p>There is no integration called “{slug}”. <Link to="/integrations">All integrations</Link></p></Page>;
  if (elsewhere) return <Page title={i.name}><OtherWorkspace orgId={elsewhere} /></Page>;
  const s = stateOf(q.data, i);
  const back = params.get('status');
  return (
    <Page title={i.name} actions={<Link className="btn" to="/integrations">All integrations</Link>}>
      <div className="int-head">
        <Logo i={i} size={40} />
        <div>
          <p className="small muted">{CATEGORIES[i.category].label}{i.vendor !== 'Outlevo' ? ` · by ${i.vendor}` : ''}</p>
          <p className="int-summary">{i.summary.t}</p>
          <p><Badge s={s.status} />{s.detail ? <span className="small muted"> {s.detail}</span> : null}</p>
        </div>
      </div>
      {back === 'connected' ? <p className="notice notice-ok" role="status">{i.name} is connected. Next: propose the field mapping (Propose mapping below), then an owner or admin approves it in Approvals.
        <button type="button" className="btn" onClick={() => setParams({}, { replace: true })}>Dismiss</button></p> : null}
      {back === 'error' ? <div className="error" role="alert"><strong>Connecting {i.name} did not finish; nothing was connected.</strong> {params.get('code') === 'access_denied' ? 'Access was not granted on the consent screen.' : params.get('code') ? `(${humanize(params.get('code')!)})` : ''}
        <button type="button" className="btn" onClick={() => setParams({}, { replace: true })}>Dismiss</button></div> : null}

      <Section title={s.connected ? 'Manage' : i.included ? 'Use it' : 'Connect'} id="connect">
        {/* "Coming soon: waiting for Meta…" beside "Connected" contradicted itself (live 2026-11-01): a connected one has no wait line */}
        {i.status && !(s.status === 'connected' && i.availability === 'awaiting_approval') ? <p className="small">{i.status.t}</p> : null}
        {s.status === 'not_set_up' ? <p className="small muted">Not set up on this server yet: Outlevo has not added this integration’s keys here, so connecting would fail.</p> : null}
        {!canConnect ? <p className="small muted">{i.who.t}</p> : null}
        <Action i={i} s={s} can={canConnect} data={q.data} />
      </Section>

      <Section title="What it does">
        {i.about.map((x) => <p key={x.t}>{x.t}</p>)}
        <ul>{i.does.map((x) => <li key={x.t}>{x.t}</li>)}</ul>
      </Section>
      <Section title="What syncs">
        <Table caption={`What syncs between Outlevo and ${i.name}`} rows={i.flows.map((f, n) => ({ id: String(n), direction: f.dir === 'in' ? `${i.name} → Outlevo` : `Outlevo → ${i.name}`, what: f.what, detail: f.fact.t }))}
          columns={[{ key: 'direction', label: 'Direction' }, { key: 'what', label: 'Data' }, { key: 'detail', label: 'Details' }]} />
      </Section>
      <Section title="Set up">
        <ol className="int-steps">{i.setup.map((x) => <li key={x.t}>{x.t}</li>)}</ol>
      </Section>
      <Section title="Access and cost">
        <dl className="kv">
          {/* .kv is a grid of pairs: each label and its value in one cell (they landed in different cells) */}
          <div><dt>Who can connect</dt><dd>{i.who.t}</dd></div>
          {i.plans ? <div><dt>Plans</dt><dd>{i.plans.t}</dd></div> : null}
          <div><dt>Cost</dt><dd>{i.pricing.t}</dd></div>
          <div><dt>Access</dt><dd>{i.permissions.map((p) => <p key={p.t} className="small">{p.t}</p>)}</dd></div>
        </dl>
        {onSite(i) ? <p className="small"><a href={`${SITE}/integrations/${i.slug}/`} target="_blank" rel="noreferrer">The {i.name} integration on outlevo.com</a></p> : null}
        {INTEGRATIONS.filter((x) => x.partOf === i.slug).map((x) => <p key={x.slug} className="small">Also part of it: <Link to={`/integrations/${x.slug}`}>{x.name}</Link> ({x.tagline.t})</p>)}
      </Section>
    </Page>
  );
}

/** The real Connect or Manage action for one integration, from the flow its feature already has. */
function Action({ i, s, can, data }: { i: Integration; s: Entry; can: boolean; data: Data | undefined }) {
  const open = i.app.manage ? <Link className="btn" to={i.app.manage.href}>{i.app.manage.label}</Link> : null;
  const offer = can && s.status !== 'coming_soon' && s.status !== 'not_set_up';
  const go = (r: { authorize_url?: string }) => { if (r.authorize_url) location.assign(r.authorize_url); };
  switch (i.app.action) {
    case 'crm': return <CrmPanel provider={i.app.provider!} name={i.name} data={data} offer={offer} />;
    case 'ad_audiences':
      return <span className="actions">{offer && !s.connected ? <ActionButton path={`/portal/ads/${i.app.provider}/connect`} label={`Connect ${i.name}`} variant="primary" onDone={go} /> : null}{open}</span>;
    case 'ad_campaigns':
      return <span className="actions">{offer ? <ActionButton path={`/portal/ad-campaigns/${i.app.provider}/connect`} label={s.connected ? `Reconnect ${i.name}` : `Connect ${i.name}`} variant={s.connected ? undefined : 'primary'} onDone={go} /> : null}{open}</span>;
    case 'smartlead': return <SmartleadPanel offer={offer} manage={open} />;
    case 'managed_sending':
      return <><p className="small">Ask the assistant to set up sending, or start it from a campaign: Outlevo proposes the domains and you pick them.</p><span className="actions"><Link className="btn btn-primary" to="/chat">Ask the assistant</Link>{open}</span></>;
    case 'linkedin': return <><LinkedinAccount /><p><span className="actions">{open}</span></p></>;
    case 'visitors': return <Load<any> path="/v1/visitors/settings">{(v) => <><VisitorSetup s={v} canEdit={can} />{open}</>}</Load>;
    case 'meetings': return <Meetings admin={can} />;
    case 'telegram': return <><p className="small">Each person links their own Telegram in Notifications.</p>{open}</>;
    case 'gmail': return <>{open}</>;
    case 'developers': return <span className="actions">{open}<a className="btn" href="https://developers.outlevo.com" target="_blank" rel="noreferrer">Developer docs</a></span>;
    case 'automation': return <span className="actions">{open}<a className="btn" href="https://developers.outlevo.com/automations.html" target="_blank" rel="noreferrer">Recipes and workflows</a></span>;
    case 'files': return <span className="actions"><Link className="btn btn-primary" to="/import">Import a CSV</Link><Link className="btn" to="/accounts">Export from Accounts</Link></span>;
    default: return open;
  }
}

function SmartleadPanel({ offer, manage }: { offer: boolean; manage: ReactNode }) {
  const a = useApi<{ status: string; mode?: string; connected_at?: string }>('/v1/outbound/account');
  return (
    <>
      {a.data ? <p className="small">{a.data.status === 'not_connected' ? 'Not connected yet.' : <><Status value={a.data.status} /> {a.data.mode === 'managed' ? 'Run by Outlevo for you' : 'Your own Smartlead account'}{a.data.connected_at ? `, connected ${when(a.data.connected_at)}` : ''}.</>}</p> : null}
      <span className="actions">
        {offer && a.data?.mode !== 'managed' ? <ActionForm title="Connect Smartlead" button={a.data?.status === 'active' ? 'Replace the key' : 'Connect Smartlead'} path="/portal/outbound/connect" fields={[
          { name: 'api_key', label: 'Smartlead API key', required: true, help: 'Stored in Vault; never shown again.' },
        ]} /> : null}
        {manage}
      </span>
    </>
  );
}

/** The CRM's connection: connect, the field mapping, contact import, sync runs and disconnect (moved from the old page). */
function CrmPanel({ provider, name, data, offer }: { provider: string; name: string; data: Data | undefined; offer: boolean }) {
  const [runs, setRuns] = useState<string | null>(null);
  const go = useNavigate();
  const rows = useMemo(() => (data?.connections ?? []).filter((c) => c.provider === provider), [data, provider]);
  const live = rows.some((r) => r.status !== 'disconnected');
  if (!data) return <Loading />;
  return (
    <>
      {offer && !live && data.available.includes(provider) ? <ActionButton path={`/portal/integrations/${provider}/connect`} label={`Connect ${name}`} variant="primary" onDone={(r) => r.authorize_url && location.assign(r.authorize_url)} /> : null}
      <Table caption={`${name} connections`} rows={rows} columns={[{ key: 'status', render: (r) => <Status value={r.status} /> }, { key: 'external_account_id', label: 'Account' }, { key: 'connected_at', label: 'Connected', render: (r) => when(r.connected_at) },
        { key: 'contacts_enabled', label: 'Contact import', render: (r) => (r.contacts_enabled ? <span title={r.contacts_purpose ?? undefined}>On{r.contacts_purpose ? `: ${r.contacts_purpose}` : ''}</span> : 'Off') },
        { key: 'id', label: 'Actions', render: (r) => (r.status === 'disconnected' ? <span className="small muted">Purged after {when(r.purge_after!)}</span> : <span className="actions">
          {offer ? <ActionButton path={`/portal/integrations/${r.id}/mapping`} label="Propose mapping" onDone={() => go('/approvals')} /> : null}
          {offer ? (r.contacts_enabled
            ? <ActionButton path={`/portal/integrations/${r.id}/contacts`} body={{ enabled: false }} label="Stop contact import" confirm="Stop reading contacts from the CRM? Contacts already imported stay." />
            : <ActionForm title={`Import contacts from ${name}`} button="Import contacts" path={`/portal/integrations/${r.id}/contacts`} transform={(f) => ({ enabled: true, purpose: f.purpose })}
              confirm="Contacts of imported companies are read from the CRM. Each one is checked for eligibility (F10) as it arrives, and nobody is contacted without it."
              fields={[{ name: 'purpose', label: 'Why Outlevo needs them', required: true, placeholder: 'e.g. Exclude existing contacts from outbound', help: 'Recorded with the change and shown here.' }]} />) : null}
          <button type="button" className="btn" aria-pressed={runs === r.id} onClick={() => setRuns(runs === r.id ? null : r.id)}>Sync runs</button>
          {offer ? <ActionForm title={`Disconnect ${name}`} button="Disconnect" variant="danger" path={`/portal/integrations/${r.id}/disconnect`} transform={(f) => ({ keep_evidence: f.keep_evidence === true })}
            confirm="Tokens are revoked at once and syncing stops. Imported CRM records are purged in 30 days."
            fields={[{ name: 'keep_evidence', label: 'Keep the ICP evidence already derived from won and lost deals', type: 'checkbox', help: 'Only the evidence Outlevo has already drawn from your CRM is kept; the imported records are still purged.' }]} /> : null}
        </span>) }]} empty={`${name} is not connected.`} />
      {runs ? <SyncRuns id={runs} provider={name} onClose={() => setRuns(null)} /> : null}
    </>
  );
}

/** A connection's backfill, incremental and reconcile runs, newest first. */
function SyncRuns({ id, provider, onClose }: { id: string; provider: string; onClose: () => void }) {
  return (
    <Section title={`${provider} sync runs`} actions={<button type="button" className="btn" onClick={onClose}>Close</button>}>
      <Load<{ data: any[] }> path={`/v1/integrations/${id}/sync-runs`}>{(d) => <Table caption={`${provider} sync runs`} rows={d.data} columns={[{ key: 'kind', label: 'Run', render: (r) => humanize(r.kind) }, { key: 'object_type', label: 'Records', render: (r) => humanize(r.object_type) }, 'status',
        { key: 'records_read', label: 'Read' }, { key: 'records_written', label: 'Written' }, { key: 'errors', label: 'Errors', render: (r) => fmt((r.errors ?? []).length) },
        { key: 'started_at', label: 'Started', render: (r) => when(r.started_at) }, { key: 'finished_at', label: 'Finished', render: (r) => (r.finished_at ? when(r.finished_at) : r.status === 'running' ? 'Running' : '—') }]} empty="No sync runs yet." />}</Load>
    </Section>
  );
}

